import React, { useState } from 'react';
import { createMatch, MatchFormData, MatchStatus } from '../api/matchesApi';
import { Team } from '../api/teamsApi';
import Papa from 'papaparse';

interface ImportMatchesModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: (count: number) => void;
  teams: Team[];
}

// Define the expected structure for CSV data
interface CSVMatch {
  source_id: string;
  game_id: string;
  group_id: string;
  Round: string;
  matchday?: string;
  home_team_id: string;
  away_team_id: string;
  home_team_name: string;
  away_team_name: string;
  match_date: string;
  status?: string;
  knockout_next_match?: string; // ID of the next match in knockout stage
  knockout_next_match_home?: string;
}

const ImportMatchesModal: React.FC<ImportMatchesModalProps> = ({
  isOpen,
  onClose,
  onSuccess,
  teams
}) => {
  const [file, setFile] = useState<File | null>(null);
  const [isUploading, setIsUploading] = useState<boolean>(false);
  const [error, setError] = useState<string>('');
  const [preview, setPreview] = useState<CSVMatch[]>([]);
  const [showPreview, setShowPreview] = useState<boolean>(false);

  // Normalize CSV headers to match our expected format
  const normalizeHeaders = (headers: string[]): string[] => {
    return headers.map(header => {
      const h = header.trim().toLowerCase();
      
      // Map different variations of matchday to our expected format
      if (h === 'matchday' || h === 'match_day' || h === 'match day') {
        return 'matchday';
      }
      
      // Other header normalizations if needed
      if (h === 'id' || h === 'gameid' || h === 'game id') {
        return 'game_id';
      }
      
      if (h === 'round' || h === 'stage') {
        return 'Round';
      }
      
      // Return the original header if no normalization was applied
      return header;
    });
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFile = e.target.files?.[0];
    if (!selectedFile) {
      setFile(null);
      setPreview([]);
      setShowPreview(false);
      return;
    }

    setFile(selectedFile);
    
    // Parse CSV for preview with header normalization
    Papa.parse<CSVMatch>(selectedFile, {
      header: true,
      transformHeader: (header) => {
        // Normalize each header as it's parsed
        const normalizedHeader = header.trim().toLowerCase();
        
        if (normalizedHeader === 'matchday' || normalizedHeader === 'match_day' || normalizedHeader === 'match day') {
          return 'matchday';
        }
        
        if (normalizedHeader === 'id' || normalizedHeader === 'gameid' || normalizedHeader === 'game id') {
          return 'game_id';
        }
        
        if (normalizedHeader === 'round' || normalizedHeader === 'stage') {
          return 'Round';
        }
        
        return header;
      },
      complete: (results) => {
        // Filter valid matches and set preview
        const validData = results.data.filter(item => item.game_id);
        setPreview(validData);
        setShowPreview(true);
      },
      error: (error) => {
        setError(`Error parsing CSV: ${error.message}`);
      }
    });
  };

  // Helper function to safely parse team ID
  const parseTeamId = (teamIdStr: string | null | undefined): number | null => {
    if (!teamIdStr || teamIdStr.trim() === '' || teamIdStr === '0') return null;
    const parsed = parseInt(teamIdStr, 10);
    return isNaN(parsed) ? null : parsed;
  };

  // Correctly parse the status value or default to SCHEDULED
  const parseStatus = (statusStr: string | undefined): string => {
    if (!statusStr) return MatchStatus.SCHEDULED;
    
    const normalized = statusStr.trim().toUpperCase();
    
    if (normalized === 'LIVE' || normalized === MatchStatus.LIVE) {
      return MatchStatus.LIVE;
    } else if (normalized === 'COMPLETED' || normalized === 'FINISHED' || normalized === MatchStatus.COMPLETED) {
      return MatchStatus.COMPLETED;
    }
    
    return MatchStatus.SCHEDULED;
  };

  const handleImport = async () => {
    if (!file) {
      setError('Please select a file first');
      return;
    }

    setIsUploading(true);
    setError('');

    try {
      const results = await new Promise<Papa.ParseResult<CSVMatch>>((resolve, reject) => {
        Papa.parse<CSVMatch>(file, {
          header: true,
          transformHeader: (header) => {
            // Use the same normalization function for consistency
            const normalizedHeader = header.trim().toLowerCase();
            
            if (normalizedHeader === 'matchday' || normalizedHeader === 'match_day' || normalizedHeader === 'match day') {
              return 'matchday';
            }
            
            if (normalizedHeader === 'id' || normalizedHeader === 'gameid' || normalizedHeader === 'game id') {
              return 'game_id';
            }
            
            if (normalizedHeader === 'round' || normalizedHeader === 'stage') {
              return 'Round';
            }
            
            return header;
          },
          complete: resolve,
          error: reject
        });
      });

      // Filter out empty rows
      const validMatches = results.data.filter(match => match.game_id);

      if (validMatches.length === 0) {
        throw new Error('No valid matches found in the CSV');
      }

      // Process and save each match
      let importedCount = 0;
      let errors: string[] = [];

      for (const csvMatch of validMatches) {
        // Correctly handle matchday parsing
        let matchday: number | null = null;
        if (csvMatch.matchday && csvMatch.matchday.trim() !== '') {
          const parsedMatchday = parseInt(csvMatch.matchday.trim(), 10);
          if (!isNaN(parsedMatchday)) {
            matchday = parsedMatchday;
          }
        }

        const knockoutNextMatchHome = csvMatch.knockout_next_match_home === 'TRUE';
        const knockoutNextMatch = csvMatch.knockout_next_match === '' ? null : csvMatch.knockout_next_match;
        const group_id = csvMatch.group_id === '' ? null : parseInt(csvMatch.group_id, 10);

        // Convert to MatchFormData
        const matchData: MatchFormData = {
          id: csvMatch.game_id,
          source_id: csvMatch.source_id,
          round: csvMatch.Round,
          matchday: matchday,
          home_team_id: parseTeamId(csvMatch.home_team_id),
          away_team_id: parseTeamId(csvMatch.away_team_id),
          home_team_placeholder: csvMatch.home_team_name || 'TBD',
          away_team_placeholder: csvMatch.away_team_name || 'TBD',
          home_team_score: null,
          away_team_score: null,
          result: '',
          status: parseStatus(csvMatch.status),
          scorers: [],
          home_team_yellow_cards: 0,
          home_team_second_yellow_cards: 0,
          home_team_red_cards: 0,
          away_team_yellow_cards: 0,
          away_team_second_yellow_cards: 0,
          away_team_red_cards: 0,
          match_date: new Date(csvMatch.match_date).toISOString(),
          group_id: group_id,
          knockout_next_match: knockoutNextMatch,
          knockout_next_match_home: knockoutNextMatchHome,
          bettable: false,
        };

        try {
          await createMatch(matchData);
          importedCount++;
        } catch (err) {
          console.error(`Error importing match ${csvMatch.game_id}:`, err);
          errors.push(`Match ${csvMatch.game_id}: ${err instanceof Error ? err.message : 'Unknown error'}`);
          // Continue with other matches even if one fails
        }
      }

      if (importedCount > 0) {
        // Success message including information about bet configurations
        const successMessage = `Imported ${importedCount} matches successfully. Bet configurations were automatically associated based on match rounds and matchdays.`;
        onSuccess(importedCount);
        
        // If there were also errors, show them
        if (errors.length > 0) {
          setError(`${successMessage} Failed to import ${errors.length} matches. First error: ${errors[0]}`);
          // Don't close modal if there were errors
          return;
        }
        
        onClose();
      } else {
        throw new Error('Failed to import any matches. ' + (errors.length > 0 ? errors[0] : ''));
      }
    } catch (err) {
      console.error('Error importing matches:', err);
      setError(err instanceof Error ? err.message : 'An unknown error occurred');
    } finally {
      setIsUploading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50 flex justify-center items-center">
      <div className="relative bg-white rounded-lg shadow-lg max-w-3xl w-full mx-4">
        <div className="flex justify-between items-center px-4 py-3 border-b">
          <h3 className="text-xl font-semibold text-gray-900">Import Matches from CSV</h3>
          <button
            type="button"
            className="text-gray-400 bg-transparent hover:bg-gray-200 hover:text-gray-900 rounded-lg p-1.5"
            onClick={onClose}
          >
            <span className="sr-only">Close</span>
            <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg">
              <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd"></path>
            </svg>
          </button>
        </div>
        
        <div className="p-6">
          {error && (
            <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4">
              {error}
            </div>
          )}
          
          <div className="mb-6">
            <p className="text-gray-700 mb-4">
              Upload a CSV file with the following columns:
              <code className="block bg-gray-100 p-2 rounded mt-2 text-xs">
                game_id,source_id,Round,matchday,home_team_id,away_team_id,home_team_name,away_team_name,match_date,status
              </code>
            </p>
            <p className="text-gray-700 mb-4">
              <strong>Notes:</strong>
              <ul className="list-disc ml-5 mt-1">
                <li>Round can be: Group Stage, Round of 16, Quarter-Final, Semi-Final, Final</li>
                <li>Leave matchday empty for knockout matches</li>
                <li>Leave team_id fields empty or set to 0 if no team is assigned yet</li>
                <li>home_team_name/away_team_name are used as placeholders when team_id is not set</li>
                <li>Date format should be: YYYY-MM-DD HH:MM:SS</li>
              </ul>
            </p>
            
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Select CSV File
            </label>
            <input
              type="file"
              accept=".csv"
              onChange={handleFileChange}
              className="block w-full text-sm text-gray-500
                file:mr-4 file:py-2 file:px-4
                file:rounded-md file:border-0
                file:text-sm file:font-semibold
                file:bg-primary-50 file:text-primary-700
                hover:file:bg-primary-100"
            />
          </div>

          {showPreview && preview.length > 0 && (
            <div className="mb-6">
              <h4 className="text-lg font-medium text-gray-900 mb-2">Preview ({preview.length} matches)</h4>
              <div className="bg-white border border-gray-200 rounded-md overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">ID</th>
                      <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Round</th>
                      <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Matchday</th>
                      <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Home Team</th>
                      <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Away Team</th>
                      <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Date</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {preview.slice(0, 5).map((match, index) => {
                      // Get team names from IDs if available
                      const homeTeamId = parseTeamId(match.home_team_id);
                      const awayTeamId = parseTeamId(match.away_team_id);
                      const homeTeam = homeTeamId !== null ? teams.find(t => t.id === homeTeamId) : null;
                      const awayTeam = awayTeamId !== null ? teams.find(t => t.id === awayTeamId) : null;
                      
                      return (
                        <tr key={index}>
                          <td className="px-3 py-2 whitespace-nowrap text-sm text-gray-500">{match.game_id}</td>
                          <td className="px-3 py-2 whitespace-nowrap text-sm text-gray-900">{match.Round}</td>
                          <td className="px-3 py-2 whitespace-nowrap text-sm text-gray-900">{match.matchday ?? ''}</td>
                          <td className="px-3 py-2 whitespace-nowrap text-sm text-gray-900">
                            {homeTeam?.name || match.home_team_name || (homeTeamId ? `Team ID: ${homeTeamId}` : 'TBD')}
                          </td>
                          <td className="px-3 py-2 whitespace-nowrap text-sm text-gray-900">
                            {awayTeam?.name || match.away_team_name || (awayTeamId ? `Team ID: ${awayTeamId}` : 'TBD')}
                          </td>
                          <td className="px-3 py-2 whitespace-nowrap text-sm text-gray-900">{match.match_date}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
                {preview.length > 5 && (
                  <div className="px-3 py-2 text-sm text-gray-500 border-t">
                    ... and {preview.length - 5} more matches
                  </div>
                )}
              </div>
            </div>
          )}
          
          <div className="flex justify-end space-x-3 border-t pt-4">
            <button
              type="button"
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md shadow-sm hover:bg-gray-50"
              onClick={onClose}
              disabled={isUploading}
            >
              Cancel
            </button>
            <button
              type="button"
              className="px-4 py-2 text-sm font-medium text-white bg-primary-600 border border-transparent rounded-md shadow-sm hover:bg-primary-700"
              onClick={handleImport}
              disabled={!file || isUploading}
            >
              {isUploading ? 'Importing...' : 'Import Matches'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ImportMatchesModal; 